import { useState, useEffect, useCallback } from 'react'
import { formatCurrency } from '@/components/charts/chartUtils'

interface AuditLogEntry {
  id: number
  username: string
  action: string
  entity_type: string
  entity_id: number
  changes: Record<string, unknown>
  timestamp: string
}

export function Settings() {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [exporting, setExporting] = useState(false)

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(`/api/audit-log?days=${days}&limit=100`)
      if (response.ok) {
        const data = (await response.json()) as { logs: AuditLogEntry[] }
        setAuditLogs(data.logs)
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    fetchAuditLogs()
  }, [fetchAuditLogs])

  const handleExport = async (format: string) => {
    setExporting(true)
    try {
      const response = await fetch(`/api/export?format=${format}`)
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download =
          response.headers
            .get('Content-Disposition')
            ?.split('filename=')[1]
            ?.replace(/"/g, '') || `export-${format}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        a.remove()
      }
    } catch (error) {
      console.error('Export error:', error)
    } finally {
      setExporting(false)
    }
  }

  const resetOnboarding = () => {
    localStorage.removeItem('onboarding_completed')
    window.location.reload()
  }

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const formatEntityType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-forest-dark">Settings</h1>
        <p className="text-sage mt-1">Manage your account and export data</p>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">Export Data</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleExport('transactions')}
            disabled={exporting}
            className="p-4 border border-stone/20 rounded-xl hover:border-forest hover:bg-forest/5 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-medium text-charcoal">Export Transactions</p>
                <p className="text-sm text-stone">
                  Download all transactions as CSV
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleExport('recurring')}
            disabled={exporting}
            className="p-4 border border-stone/20 rounded-xl hover:border-forest hover:bg-forest/5 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔄</span>
              <div>
                <p className="font-medium text-charcoal">
                  Export Recurring Items
                </p>
                <p className="text-sm text-stone">
                  Download recurring templates as CSV
                </p>
              </div>
            </div>
          </button>
        </div>
        {exporting && (
          <p className="text-sm text-sage mt-4 text-center">
            Generating export...
          </p>
        )}
      </div>

      {/* Preferences Section */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
        <h2 className="text-lg font-semibold text-charcoal mb-4">Preferences</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-cream/50 rounded-xl">
            <div>
              <p className="font-medium text-charcoal">Reset Onboarding</p>
              <p className="text-sm text-stone">
                Show the welcome tour again on next visit
              </p>
            </div>
            <button
              onClick={resetOnboarding}
              className="px-4 py-2 border border-stone/20 rounded-lg text-sm text-charcoal hover:bg-stone/5 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Audit Log Section */}
      <div className="bg-white rounded-xl shadow-sm border border-stone/10">
        <div className="p-4 border-b border-stone/10 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-charcoal">Activity Log</h2>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-1 border border-stone/20 rounded-lg text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sage">Loading activity log...</div>
        ) : auditLogs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sage">No activity recorded</p>
            <p className="text-sm text-stone mt-2">
              Activity will appear here as you use the app
            </p>
          </div>
        ) : (
          <div className="divide-y divide-stone/10 max-h-96 overflow-y-auto">
            {auditLogs.map((log) => (
              <div
                key={log.id}
                className="p-4 hover:bg-cream/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-forest/10 flex items-center justify-center text-forest text-sm">
                      {log.username?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm">
                        <span className="font-medium text-charcoal">
                          {log.username || 'System'}
                        </span>
                        <span className="text-stone">
                          {' '}
                          {formatAction(log.action).toLowerCase()}{' '}
                        </span>
                        <span className="text-charcoal">
                          {formatEntityType(log.entity_type)}
                        </span>
                        {log.entity_id > 0 && (
                          <span className="text-stone"> #{log.entity_id}</span>
                        )}
                      </p>
                      {log.changes && typeof log.changes === 'object' && (
                        <p className="text-xs text-stone mt-1 truncate max-w-md">
                          {Object.entries(log.changes)
                            .slice(0, 3)
                            .map(
                              ([key, value]) =>
                                `${key}: ${typeof value === 'number' ? formatCurrency(value as number) : value}`
                            )
                            .join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-stone whitespace-nowrap">
                    {formatTimestamp(log.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
